import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import { useTheme } from "next-themes";
import Iridescence from "./Iridescence";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Package, ShieldCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { IRIDESCENCE_DARK, IRIDESCENCE_LIGHT } from "../lib/iridescence";
import { formatPrice, isValidEmail } from "../lib/utils";

const MemoIridescence = React.memo(Iridescence);

const CartPage = () => {
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const { selectedPlan, items, email, setEmail } = useCart();
  const isDark = resolvedTheme === "dark";
  const iridescenceColor = isDark ? IRIDESCENCE_DARK : IRIDESCENCE_LIGHT;
  const hasValidEmail = isValidEmail(email);
  const hasIphoneItems = items.some((item) => item.category === "iPhones");
  const hasOtherItems = items.some((item) => item.category !== "iPhones");

  const cartTotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  );
  const totalWithPlan = cartTotal + (selectedPlan?.price || 0);
  const buildItemsSummary = (filterFn) => {
    const filtered = items.filter(filterFn);
    if (!filtered.length) return "";
    return filtered
      .map((item) => {
        const variant = item.variantLabel ? ` (${item.variantLabel})` : "";
        return `${item.quantity}x ${item.name}${variant}`;
      })
      .join(", ");
  };

  return (
    <PageLayout
      className="relative bg-gradient-to-br from-[#fff5e9] via-[#fffaf6] to-[#ffe3c4] dark:from-[#2a0e08] dark:via-[#3b140d] dark:to-[#1f0a08]"
      before={
        <div
          className={`absolute inset-0 pointer-events-none ${isDark ? "opacity-18" : "opacity-28"}`}
          key={`cart-iridescence-${resolvedTheme}`}
        >
          <MemoIridescence color={iridescenceColor} speed={0.55} amplitude={isDark ? 0.1 : 0.12} />
        </div>
      }
    >
      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 md:py-14">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate("/tienda")}
            className="text-sm text-muted-foreground hover:text-foreground w-fit dark:text-[#d6bfae] dark:hover:text-[#f7e9dd]"
          >
            &larr; Volver a comprar
          </button>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Finalizar Compra
          </h1>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Card className="plan-card rounded-2xl shadow-xl backdrop-blur bg-white/95 dark:bg-[#3a1b14]/90">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg md:text-xl text-foreground dark:text-[#f7e9dd]">Información de Contacto</CardTitle>
              <p className="text-sm text-muted-foreground dark:text-[#d6bfae]">
                Te enviaremos el comprobante a este email
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              <label className="text-sm font-medium text-foreground dark:text-[#f7e9dd]">Email *</label>
              <Input
                type="email"
                placeholder="tu@email.com"
                className="h-11 border-border bg-white text-foreground placeholder:text-muted-foreground focus-visible:ring-ring/30 dark:border-[#6a3522] dark:bg-[#2f1712] dark:text-[#f7e9dd] dark:placeholder:text-[#b79d8c] dark:focus-visible:ring-[#ff7a3d]/50"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </CardContent>
          </Card>

          {hasValidEmail ? (
            <div className="space-y-4">
              <Card className="plan-card rounded-2xl shadow-lg bg-white/95 dark:bg-[#3a1b14]/90">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-lg md:text-xl text-foreground dark:text-[#f7e9dd]">Resumen del Pedido</CardTitle>
                  <p className="text-sm text-muted-foreground dark:text-[#d6bfae]">
                    Revisa tus items antes de pagar
                  </p>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-foreground dark:text-[#f7e9dd]">
                  {selectedPlan && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground dark:text-[#d6bfae]">1x {selectedPlan.name}</span>
                      <span className="font-semibold">{formatPrice(selectedPlan.price)}</span>
                    </div>
                  )}
                  {items.length ? (
                    items.map((item) => (
                      <div key={`${item.id}-${item.variantKey}`} className="flex items-center justify-between">
                        <span className="text-muted-foreground dark:text-[#d6bfae]">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="font-semibold">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground dark:text-[#d6bfae]">No hay productos en el carrito.</p>
                  )}
                  <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold dark:border-[#5a2a1b]">
                    <span>Total</span>
                    <span>{formatPrice(totalWithPlan)}</span>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center gap-2 rounded-xl border border-border bg-white/90 px-4 py-3 text-sm text-muted-foreground dark:border-[#5a2a1b] dark:bg-[#2e140f] dark:text-[#d6bfae]">
                <ShieldCheck className="h-4 w-4 text-foreground dark:text-[#f7e9dd]" />
                Pagos procesados de forma segura por Mercado Pago
              </div>

              <Card className="plan-card rounded-2xl shadow-lg bg-white/95 dark:bg-[#3a1b14]/90">
                <CardHeader className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg md:text-xl text-foreground dark:text-[#f7e9dd]">Finalizar Compra</CardTitle>
                    <span className="rounded-full border border-border/60 bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                      Próximamente
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground dark:text-[#d6bfae]">Elige tu medio de pago favorito</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    disabled
                    className="w-full rounded-xl bg-[#00A1E4] text-white shadow-[0_8px_20px_rgba(0,161,228,0.35)] hover:bg-[#008FCB] dark:bg-[#00A1E4] dark:hover:bg-[#008FCB] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Pagar con Mercado Pago
                  </Button>
                  <Button
                    variant="outline"
                    disabled
                    className="w-full rounded-xl border-[#b38fff] text-[#7f55e6] hover:bg-[#b38fff]/10 dark:border-[#d6bfae] dark:text-[#f7e9dd] dark:hover:bg-[#2e140f] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Pagar con Transferencia
                  </Button>
                  {(hasOtherItems || hasIphoneItems) && (
                    <div className="pt-2 space-y-2">
                      {hasOtherItems && (
                        <Button
                          asChild
                          className="w-full rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_8px_20px_rgba(16,185,129,0.35)]"
                        >
                          <a
                            href={`https://wa.me/2355544386?text=${encodeURIComponent(
                              `Hola, vengo desde la página web y quiero finalizar mi compra de componentes por WhatsApp. Productos: ${buildItemsSummary(
                                (item) => item.category !== "iPhones"
                              )}.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Finalizar compra por WhatsApp (Componentes)
                          </a>
                        </Button>
                      )}
                      {hasIphoneItems && (
                        <Button
                          asChild
                          className="w-full rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_8px_20px_rgba(16,185,129,0.35)]"
                        >
                          <a
                            href={`https://wa.me/2355514955?text=${encodeURIComponent(
                              `Hola, vengo desde la página web y quiero finalizar mi compra de iPhone por WhatsApp. Productos: ${buildItemsSummary(
                                (item) => item.category === "iPhones"
                              )}.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Finalizar compra por WhatsApp (iPhone)
                          </a>
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="plan-card rounded-2xl border-dashed shadow-lg bg-white/90 dark:bg-[#3a1b14]/80">
              <CardContent className="flex flex-col items-center justify-center gap-2 py-10 text-center min-h-[200px]">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-white/90 dark:border-[#5a2a1b] dark:bg-[#2e140f]">
                  <Package className="h-7 w-7 text-muted-foreground dark:text-[#d6bfae]" />
                </div>
                <p className="text-sm font-semibold text-foreground dark:text-[#f7e9dd]">Ingresa tu email</p>
                <p className="text-sm text-muted-foreground dark:text-[#d6bfae]">para continuar con el pago</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default CartPage;
